import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useMemo } from 'react';
import { Table, Card, Button, Input, Tag, Space, Switch, Typography, Tooltip } from 'antd';
import { ArrowRightOutlined, ArrowLeftOutlined, SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

const UnifiedTransferList = ({ 
    unifiedRecords, 
    selectedItems, 
    onSelectChange,
    remainingAmount, 
    hasSurplus,
    currentReglementRef,
    reglementAmount, // MNT_REGLER - the initial payment amount
    surplusCreated // Array of surplus created from this reglement
}) => {
    const [searchText, setSearchText] = useState('');
    const [showFullySettled, setShowFullySettled] = useState(false);
    const [localUnifiedRecords, setLocalUnifiedRecords] = useState(unifiedRecords);

    const sanitizedSelectedItems = useMemo(() => (
        (selectedItems || []).map(item => ({
            ...item,
            MNT_REGLER: Math.abs(item.MNT_REGLER || 0),
            MNT_ORIGINAL: Math.abs(item.MNT_ORIGINAL || item.MNT_TTC || 0)
        }))
    ), [selectedItems]);

    // Calculate current remaining amount from selected items
    const calculateCurrentRemainingAmount = (items) => {
        const facturesTotal = items
            .filter(item => item.REF_AV_FAC.startsWith('FA'))
            .reduce((sum, item) => sum + Math.abs(item.MNT_REGLER || 0), 0);
        
        const creditsTotal = items
            .filter(item => item.REF_AV_FAC.startsWith('AV') || item.REF_AV_FAC.startsWith('SUR'))
            .reduce((sum, item) => sum + Math.abs(item.MNT_REGLER || 0), 0);
        
        return (reglementAmount || 0) - facturesTotal + creditsTotal;
    };

    // Transfer single item to right (selected items)
    const handleTransferItemToRight = (record) => {
        if (hasSurplus || isUsedInReglement(record) || isFullySettled(record)) {
            return;
        }

        const currentRemainingAmount = calculateCurrentRemainingAmount(sanitizedSelectedItems);
        
        if (record.REF_AV_FAC.startsWith('FA') && currentRemainingAmount <= 0) {
            return;
        }

        let newRemainingAmount = currentRemainingAmount;
        const newUnifiedRecords = [...localUnifiedRecords];
        const newSelectedItems = [...sanitizedSelectedItems];
        
        const unifiedIndex = newUnifiedRecords.findIndex(item => item.REF_AV_FAC === record.REF_AV_FAC);
        if (unifiedIndex === -1) return;

        const isCredit = record.REF_AV_FAC.startsWith('AV') || record.REF_AV_FAC.startsWith('SUR');
        let transferAmount;

        if (isCredit) {
            const alreadyRegistered = Math.abs(record.MNT_REGLER || 0);
            const totalCreditAmount = Math.abs(record.MNT_TTC || 0);
            const availableCredit = totalCreditAmount - alreadyRegistered;
            if (availableCredit <= 0) {
                return;
            }
            transferAmount = availableCredit;
            newRemainingAmount += transferAmount;
        } else {
            const remainingInvoiceAmount = Math.max((record.MNT_TTC || 0) - (record.MNT_REGLER || 0), 0);
            transferAmount = Math.min(remainingInvoiceAmount, newRemainingAmount);
            if (transferAmount <= 0) return;
            newRemainingAmount -= transferAmount;
        }

        // Update unified record's MNT_REGLER
        newUnifiedRecords[unifiedIndex] = {
            ...record,
            MNT_REGLER: (Math.abs(record.MNT_REGLER || 0)) + transferAmount
        };

        // Update or create selected item
        const existingDetailIndex = newSelectedItems.findIndex(item => item.REF_AV_FAC === record.REF_AV_FAC);
        if (existingDetailIndex !== -1) {
            newSelectedItems[existingDetailIndex] = {
                ...newSelectedItems[existingDetailIndex],
                MNT_REGLER: Math.abs(newSelectedItems[existingDetailIndex].MNT_REGLER || 0) + transferAmount
            };
        } else {
            newSelectedItems.push({
                REF_AV_FAC: record.REF_AV_FAC,
                MNT_ORIGINAL: Math.abs(record.MNT_TTC || 0),
                MNT_REGLER: transferAmount,
                DATE_DOC: new Date()
            });
        }

        setLocalUnifiedRecords(newUnifiedRecords);
        onSelectChange(newSelectedItems, newUnifiedRecords);
    };

    // Transfer single item back to left (available items)
    const handleTransferItemToLeft = (selectedItem) => {
        if (hasSurplus) {
            return;
        }

        const newUnifiedRecords = [...localUnifiedRecords];
        const newSelectedItems = [...sanitizedSelectedItems];
        
        const key = selectedItem.REF_AV_FAC;

        // Update unified record
        const unifiedIndex = newUnifiedRecords.findIndex(item => item.REF_AV_FAC === key);
        if (unifiedIndex !== -1) {
            const currentMntRegler = Math.abs(newUnifiedRecords[unifiedIndex].MNT_REGLER || 0);
            newUnifiedRecords[unifiedIndex] = {
                ...newUnifiedRecords[unifiedIndex],
                MNT_REGLER: Math.max(currentMntRegler - Math.abs(selectedItem.MNT_REGLER || 0), 0)
            };
        }

        // Remove or reduce selected item
        const detailIndex = newSelectedItems.findIndex(item => item.REF_AV_FAC === key);
        if (detailIndex !== -1) {
            const currentDetailAmount = Math.abs(newSelectedItems[detailIndex].MNT_REGLER || 0);
            const selectedAmount = Math.abs(selectedItem.MNT_REGLER || 0);
            if (selectedAmount === currentDetailAmount) {
                // Remove if transferring full amount
                newSelectedItems.splice(detailIndex, 1);
            } else {
                // Reduce amount if partial transfer
                newSelectedItems[detailIndex] = {
                    ...newSelectedItems[detailIndex],
                    MNT_REGLER: Math.max(currentDetailAmount - selectedAmount, 0)
                };
            }
        }

        setLocalUnifiedRecords(newUnifiedRecords);
        onSelectChange(newSelectedItems, newUnifiedRecords);
    };

    // Update local unified records when prop changes
    React.useEffect(() => {
        const normalizedRecords = (unifiedRecords || []).map(record => ({
            ...record,
            MNT_REGLER: Math.abs(record.MNT_REGLER || 0),
            MNT_TTC: record.MNT_TTC || 0
        }));
        setLocalUnifiedRecords(normalizedRecords);
    }, [unifiedRecords]);

    // Helper function to check if a document (facture, avoir, or surplus) is fully settled
    const isFullySettled = (record) => {
        const totalAmount = Math.abs(record.MNT_TTC || 0);
        const registeredAmount = Math.abs(record.MNT_REGLER || 0);
        const availableAmount = totalAmount - registeredAmount;
        
        // For factures: fully settled when availableAmount <= 0
        if (record.REF_AV_FAC.startsWith('FA')) {
            return availableAmount <= 0;
        }
        
        // For avoirs and surplus: fully settled when the entire credit has been used
        if (record.REF_AV_FAC.startsWith('AV') || record.REF_AV_FAC.startsWith('SUR')) {
            return availableAmount <= 0;
        }
        
        return false;
    };

    // Helper function to check if item is already used in selected items
    const isUsedInReglement = (record) => {
        return selectedItems.some(item => item.REF_AV_FAC === record.REF_AV_FAC);
    };

    // Helper function to check if surplus is from current reglement
    const isSurplusFromCurrentReglement = (record) => {
        if (!record.REF_AV_FAC.startsWith('SUR')) return false;
        if (!record.surplus?.reglement) return false;
        return record.surplus.reglement.REF_REGV === currentReglementRef;
    };

    // Filter records based on search text and fully settled toggle
    const filteredUnifiedRecords = React.useMemo(() => {
        let filtered = localUnifiedRecords;
        
        // Filter out fully settled documents (factures, avoirs, surplus) unless toggle is on
        if (!showFullySettled) {
            filtered = filtered.filter(record => !isFullySettled(record));
        }
        
        // Filter by search text
        if (searchText) {
            filtered = filtered.filter(record => 
                record.REF_AV_FAC.toLowerCase().includes(searchText.toLowerCase())
            );
        }
        
        return filtered;
    }, [localUnifiedRecords, searchText, showFullySettled]);

    const leftColumns = [
        {
            title: 'Action',
            key: 'action',
            width: 80,
            render: (_, record) => {
                const isFullySettledRecord = isFullySettled(record);
                const isUsed = isUsedInReglement(record);
                const isDisabled = hasSurplus || isUsed || isFullySettledRecord || 
                    (record.REF_AV_FAC.startsWith('FA') && remainingAmount <= 0);
                
                return (
                    <PermissionButton permissionModule="payments" permissionAction="allocate"
                        type="text"
                        icon={<ArrowRightOutlined />}
                        onClick={() => handleTransferItemToRight(record)}
                        disabled={isDisabled}
                        style={{ 
                            opacity: isDisabled ? 0.3 : 1,
                            cursor: isDisabled ? 'not-allowed' : 'pointer'
                        }}
                    />
                );
            },
        },
        {
            title: 'Référence',
            dataIndex: 'REF_AV_FAC',
            render: (text, record) => {
                const isFullySettledRecord = isFullySettled(record);
                const isUsed = isUsedInReglement(record);
                const isSurplus = record.REF_AV_FAC.startsWith('SUR');
                const isSurplusFromCurrent = isSurplusFromCurrentReglement(record);
                const surplusSourceReglement = record.surplus?.reglement?.REF_REGV;
                
                return (
                    <Space style={{ opacity: (isFullySettledRecord || (isUsed && !isSurplusFromCurrent)) ? 0.5 : 1 }}>
                        {text}
                        {record.REF_AV_FAC.startsWith('AV') && <Tag color="orange">Avoir</Tag>}
                        {record.REF_AV_FAC.startsWith('FA') && <Tag color="green">Facture</Tag>}
                        {isSurplus && (
                            <>
                                <Tag color="purple">Surplus</Tag>
                                {surplusSourceReglement && !isSurplusFromCurrent && (
                                    <Tooltip title={`Créé par le règlement ${surplusSourceReglement}`}>
                                        <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '12px', cursor: 'help' }} />
                                    </Tooltip>
                                )}
                                {isSurplusFromCurrent && (
                                    <Tag color="cyan" style={{ fontSize: '11px' }}>Créé ici</Tag>
                                )}
                            </>
                        )}
                        {isFullySettledRecord && <Tag color="default">Entièrement Réglé</Tag>}
                        {isUsed && !isSurplusFromCurrent && <Tag color="grey">Déjà Utilisé</Tag>}
                    </Space>
                );
            },
        },
        {
            title: 'Montant Original',
            dataIndex: 'MNT_TTC',
            render: (value, record) => {
                const isFullySettledRecord = isFullySettled(record);
                const isUsed = isUsedInReglement(record);
                const isCredit = record.REF_AV_FAC.startsWith('AV') || record.REF_AV_FAC.startsWith('SUR');
                const displayValue = Math.abs(value);
                return (
                    <span style={{ opacity: (isFullySettledRecord || isUsed) ? 0.5 : 1 }}>
                        {isCredit ? '+' : '-'} MAD {displayValue.toFixed(2)}
                    </span>
                );
            },
        },
        {
            title: 'Montant Enregistré',
            dataIndex: 'MNT_REGLER',
            render: (value, record) => {
                const isFullySettledRecord = isFullySettled(record);
                const isUsed = isUsedInReglement(record);
                return (
                    <span style={{ opacity: (isFullySettledRecord || isUsed) ? 0.5 : 1 }}>
                        MAD {Math.abs(value || 0).toFixed(2)}
                    </span>
                );
            },
        },
        {
            title: 'Montant Restant',
            render: record => {
                const isCredit = record.REF_AV_FAC.startsWith('AV') || record.REF_AV_FAC.startsWith('SUR');
                const totalAmount = Math.abs(record.MNT_TTC || 0);
                const registeredAmount = Math.abs(record.MNT_REGLER || 0);
                const remainingAmountForRecord = Math.max(totalAmount - registeredAmount, 0);
                const isFullySettledRecord = isFullySettled(record);
                const isUsed = isUsedInReglement(record);
                return (
                    <span style={{ opacity: (isFullySettledRecord || isUsed) ? 0.5 : 1 }}>
                        {isCredit ? '+' : '-'} MAD {remainingAmountForRecord.toFixed(2)}
                    </span>
                );
            },
        }
    ];

    const rightColumns = [
        {
            title: 'Action',
            key: 'action',
            width: 80,
            render: (_, record) => {
                const isDisabled = hasSurplus;
                
                return (
                    <PermissionButton permissionModule="payments" permissionAction="allocate"
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => handleTransferItemToLeft(record)}
                        disabled={isDisabled}
                        style={{ 
                            opacity: isDisabled ? 0.3 : 1,
                            cursor: isDisabled ? 'not-allowed' : 'pointer'
                        }}
                    />
                );
            },
        },
        {
            title: 'Référence',
            dataIndex: 'REF_AV_FAC',
            render: (text, record) => {
                return (
                    <Space>
                        {text}
                        {record.REF_AV_FAC.startsWith('AV') && <Tag color="orange">Avoir</Tag>}
                        {record.REF_AV_FAC.startsWith('FA') && <Tag color="green">Facture</Tag>}
                        {record.REF_AV_FAC.startsWith('SUR') && (
                            <Tag color="purple">Surplus</Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Montant à Enregistrer',
            dataIndex: 'MNT_REGLER',
            render: (value, record) => {
                const isCredit = record.REF_AV_FAC.startsWith('AV') || record.REF_AV_FAC.startsWith('SUR');
                const displayValue = Math.abs(value);
                return (
                    <span>
                        {isCredit ? '+' : '-'} MAD {displayValue.toFixed(2)}
                    </span>
                );
            },
        }
    ];


    // Calculate totals for left side (available records)
    const leftTotal = useMemo(() => {
        return filteredUnifiedRecords.reduce((sum, record) => {
            const totalAmount = Math.abs(record.MNT_TTC || 0);
            const registeredAmount = Math.abs(record.MNT_REGLER || 0);
            const remainingForRecord = Math.max(totalAmount - registeredAmount, 0);

            if (record.REF_AV_FAC.startsWith('FA')) {
                return sum - remainingForRecord;
            }

            if (record.REF_AV_FAC.startsWith('AV') || record.REF_AV_FAC.startsWith('SUR')) {
                return sum + remainingForRecord;
            }

            return sum;
        }, 0);
    }, [filteredUnifiedRecords]);

    // Organize selected items by type
    const organizedItems = useMemo(() => {
        const factures = sanitizedSelectedItems.filter(item => item.REF_AV_FAC.startsWith('FA'));
        const avoirs = sanitizedSelectedItems.filter(item => item.REF_AV_FAC.startsWith('AV'));
        const surplus = sanitizedSelectedItems.filter(item => item.REF_AV_FAC.startsWith('SUR') && 
            !surplusCreated?.some(s => s.REF_SURPLUS === item.REF_AV_FAC));
        
        return { factures, avoirs, surplus };
    }, [sanitizedSelectedItems, surplusCreated]);

    // Calculate remaining amount
    // Formula: Montant à Régler - Factures + Avoirs + Surplus(from others) = Remaining
    // If Remaining > 0, that becomes Surplus Créé
    const calculatedRemaining = useMemo(() => {
        const facturesTotal = organizedItems.factures.reduce((sum, item) => sum + Math.abs(item.MNT_REGLER || 0), 0);
        const avoirsTotal = organizedItems.avoirs.reduce((sum, item) => sum + Math.abs(item.MNT_REGLER || 0), 0);
        const surplusTotal = organizedItems.surplus.reduce((sum, item) => sum + Math.abs(item.MNT_REGLER || 0), 0);
        
        // Start with payment amount, subtract factures, add avoirs and surplus
        return (reglementAmount || 0) - facturesTotal + avoirsTotal + surplusTotal;
    }, [organizedItems, reglementAmount]);

    return (
        <Card>
            <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                <Input
                    placeholder="Rechercher des références..."
                    prefix={<SearchOutlined />}
                    onChange={e => setSearchText(e.target.value)}
                    style={{ width: 300 }}
                />
                <Space>
                    <span>Afficher les documents entièrement réglés:</span>
                    <Switch 
                        checked={showFullySettled} 
                        onChange={setShowFullySettled}
                    />
                </Space>
            </Space>
            <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                    <Table
                        columns={leftColumns}
                        dataSource={filteredUnifiedRecords}
                        rowKey="REF_AV_FAC"
                        size="small"
                        rowClassName={(record) => {
                            if (isFullySettled(record) || isUsedInReglement(record)) {
                                return 'greyed-out-row';
                            }
                            return '';
                        }}
                    />
                    <div style={{ marginTop: 12, textAlign: 'right', paddingRight: 8 }}>
                        <Text strong>Total Disponible: </Text>
                        <Text strong style={{ fontSize: '14px' }}>
                            {leftTotal >= 0 ? '+' : ''}MAD {Math.abs(leftTotal).toFixed(2)}
                        </Text>
                    </div>
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ border: '1px solid #d9d9d9', borderRadius: '4px', padding: '12px' }}>
                        {/* Montant à Régler */}
                        <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #e8e8e8' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text strong>Montant à Régler:</Text>
                                <Text strong style={{ fontSize: '14px', color: '#52c41a' }}>
                                    + MAD {(reglementAmount || 0).toFixed(2)}
                                </Text>
                            </div>
                        </div>

                        {/* Factures Section */}
                        {organizedItems.factures.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong style={{ display: 'block', marginBottom: 8, color: '#1890ff' }}>
                                    Factures:
                                </Text>
                                <Table
                                    columns={rightColumns}
                                    dataSource={organizedItems.factures}
                                    rowKey="REF_AV_FAC"
                                    size="small"
                                    pagination={false}
                                    showHeader={false}
                                />
                            </div>
                        )}

                        {/* Avoirs Section */}
                        {organizedItems.avoirs.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong style={{ display: 'block', marginBottom: 8, color: '#fa8c16' }}>
                                    Avoirs:
                                </Text>
                                <Table
                                    columns={rightColumns}
                                    dataSource={organizedItems.avoirs}
                                    rowKey="REF_AV_FAC"
                                    size="small"
                                    pagination={false}
                                    showHeader={false}
                                />
                            </div>
                        )}

                        {/* Surplus Section (from other reglements) */}
                        {organizedItems.surplus.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong style={{ display: 'block', marginBottom: 8, color: '#722ed1' }}>
                                    Surplus:
                                </Text>
                                <Table
                                    columns={rightColumns}
                                    dataSource={organizedItems.surplus}
                                    rowKey="REF_AV_FAC"
                                    size="small"
                                    pagination={false}
                                    showHeader={false}
                                />
                            </div>
                        )}

                        {/* Montant Restant */}
                        <div style={{ marginTop: 16, marginBottom: 16, paddingTop: 12, borderTop: '1px solid #e8e8e8' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text strong>Montant Restant:</Text>
                                <Text 
                                    strong 
                                    style={{ 
                                        fontSize: '14px',
                                        color: calculatedRemaining >= 0 ? '#52c41a' : '#ff4d4f'
                                    }}
                                >
                                    {calculatedRemaining >= 0 ? '+' : ''}MAD {Math.abs(calculatedRemaining).toFixed(2)}
                                </Text>
                            </div>
                        </div>

                        {/* Surplus Créé Section (if exists) */}
                        {surplusCreated && surplusCreated.length > 0 && (
                            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '2px solid #722ed1' }}>
                                <Text strong style={{ display: 'block', marginBottom: 8, color: '#722ed1' }}>
                                    Surplus Créé:
                                </Text>
                                {surplusCreated.map((surplus) => {
                                    const surplusDetail = selectedItems.find(
                                        item => item.REF_AV_FAC === surplus.REF_SURPLUS
                                    );
                                    return (
                                        <div 
                                            key={surplus.id} 
                                            style={{ 
                                                marginBottom: 8, 
                                                padding: 8, 
                                                backgroundColor: '#f9f0ff',
                                                borderRadius: '4px'
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Space>
                                                    <Text>{surplus.REF_SURPLUS}</Text>
                                                    <Tag color="purple">Surplus</Tag>
                                                </Space>
                                                <Text strong style={{ color: '#722ed1' }}>
                                                    + MAD {surplus.MNT_SURPLUS.toFixed(2)}
                                                </Text>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <style>{`
                .greyed-out-row {
                    background-color: #f5f5f5 !important;
                    opacity: 0.6;
                }
                .greyed-out-row:hover {
                    background-color: #e8e8e8 !important;
                }
            `}</style>
        </Card>
    );
};

export default UnifiedTransferList;